import { booleanAttribute, ChangeDetectionStrategy, Component, input } from '@angular/core';
import { Tag, UploadTags } from '../upload.service';

@Component({
  selector: 'app-upload-tags',
  templateUrl: './upload-tags.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UploadTagsComponent {
  /**
   * Data upload tags.
   */
  public data = input<UploadTags>();

  /**
   * Adds custom tag when tag is valid.
   *
   * @param nameInput Custom tag name input.
   * @param valueInput Custom tag value input.
   */
  protected addTag(nameInput: HTMLInputElement, valueInput: HTMLInputElement): void {
    // grab values
    const name: string = nameInput.value;
    const value: string = valueInput.value;

    // clear validation
    nameInput.setCustomValidity('');
    valueInput.setCustomValidity('');

    if ((!name && !value) // nothing to process
      || !this.validate(name, nameInput, value, valueInput)) // invalid name or/and value
      return;

    // add valid tag
    const tag: Tag = {
      name: name,
      value: value
    };
    this.data()!.tags.push(tag);

    // clear inputs
    nameInput.value = null!;
    valueInput.value = null!;
  }

  /**
   * Validate custom tag name and tag value.
   * Sets inputs validation status.
   *
   * @param name Custom tag name.
   * @param nameInput Custom tag name input.
   * @param value Custom tag value.
   * @param valueInput Custom tag value input.
   * @returns True when custom tag name and value are valid, otherwise - false.
   */
  private validate(name: string, nameInput: HTMLInputElement, value: string, valueInput: HTMLInputElement): boolean {
    // validate name
    if (name) {
      if (!this.validateName(name, nameInput))
        return false;

      if (!value) {
        valueInput.setCustomValidity('Custom tag value is required.');
        return false;
      }
    }

    // validate value
    if (value) {
      if (!name) {
        nameInput.setCustomValidity('Custom tag name is required.');
        return false;
      }

      if (!this.validateValue(value, valueInput))
        return false;

      nameInput.setCustomValidity('');
    }

    return true;
  }

  /**
   * Validates custom tag name.
   * Sets input validation status.
   *
   * @param name Custom tag name.
   * @param nameInput Custom tag name input.
   * @returns True when custom tag name is valid, otherwise - false.
   */
  private validateName(name: string, nameInput: HTMLInputElement): boolean {
    if (name.length > this.data()!.nameMaxLength) {
      nameInput.setCustomValidity('Custom tag name is too long.');
      return false;
    }

    const regex = new RegExp(this.data()!.namePattern);
    if (!regex.test(name)) {
      nameInput.setCustomValidity('Custom tag name contains invalid characters.');
      return false;
    }

    if (this.data()!.tags.some(t => t.name === name)) {
      nameInput.setCustomValidity('Custom tag with the same name already exists.');
      return false;
    }

    nameInput.setCustomValidity('');

    return true;
  }

  /**
   * Validates custom tag value.
   * Sets input validation status.
   *
   * @param value Custom tag value.
   * @param valueInput Custom tag value input.
   * @returns True when value is valid, otherwise - false.
   */
  private validateValue(value: string, valueInput: HTMLInputElement): boolean {
    if (value.length > this.data()!.valueMaxLength) {
      valueInput.setCustomValidity('Custom tag value is too long.');
      return false;
    }

    const regex = new RegExp(this.data()!.valuePattern);
    if (!regex.test(value)) {
      valueInput.setCustomValidity('Custom tag value contains invalid characters.');
      return false;
    }

    return true;
  }

  /**
   * Removes custom tag.
   *
   * @param index Index ot the element in custom tags array.
   */
  protected removeTag(index: number): void {
    this.data()!.tags.splice(index, 1);
  }

  /**
   * Specifies if component is disabled.
   */
  public disabled = input(false, { transform: booleanAttribute });
}
